import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Page } from './../../shared/models/page';
import { Injectable } from '@angular/core';
import { Rsvp } from '../models/rsvp';
import { List } from 'immutable';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { RsvpHttpService } from './rsvp.http.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { ComparatorUtils } from '../utils/comparator-utils';
import { RoomStaySummary } from '../models/room-stay-summary';
import { TranslateService } from '@ngx-translate/core';
import { TransactionUtils } from '../utils/transaction-utils';
import { Constants } from '../../shared/constants';
import { AbstractEventHasItemsStore } from './abstract-event-has-items.store';
import { Response } from '@angular/http';
import { EventHttpService } from './event.http.service';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { EventHasItemsByVendor } from '../models/event-has-items-by-vendor';
import Decimal from 'decimal.js';
import { MathUtils } from '../utils/math-utils';

@Injectable()
export class RsvpStore extends AbstractEventHasItemsStore {
  private rsvpSubject: ReplaySubject<List<Rsvp>> = new ReplaySubject<List<Rsvp>>(1);
  private searchResultsPage: BehaviorSubject<Page<Rsvp>> = new BehaviorSubject(Page.empty<Rsvp>());

  constructor(private rsvpHttpService: RsvpHttpService,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService,
            private eventHttpService: EventHttpService) {
    super();
  }

  get searchResults(): Observable<Page<Rsvp>> {
    return this.searchResultsPage.asObservable();
  }

  get rsvps(): Observable<List<Rsvp>> {
    return this.rsvpSubject.asObservable().map(rsvpList => rsvpList.sort(
      (rsvp1: Rsvp, rsvp2: Rsvp) => ComparatorUtils.compareRsvps(rsvp1, rsvp2)).toList());
  }

  get totalNights(): Observable<number> {
    return this.rsvps.map((rsvpList: List<Rsvp>): number => {
      return rsvpList
        .map(rsvp => rsvp.nights)
        .reduce((sum: number, current: number) => !!current ? sum + current : sum, 0);
    });
  }

  get totalRevenue(): Observable<number> {
    return this.rsvps.map(rsvpList => {
      const totalRevenue = rsvpList
        .map(rsvp => rsvp.revenue)
        .reduce((sum: Decimal, currentRevenue: number) => !!currentRevenue ? sum.add(currentRevenue) : sum, new Decimal(0));
      return MathUtils.roundDecimalCurrencyToCents(totalRevenue).toNumber();
    });
  }

  vendorTotalRevenue(eventPartyId: number): Observable<number> {
    return this.rsvps.map((filteredList: List<Rsvp>) => {
      return filteredList.filter((filtered: Rsvp) => filtered.eventPartyId === eventPartyId).toList();
    }).map(rsvpList => {
      const totalRevenue = rsvpList
        .map(rsvp => rsvp.revenue)
        .reduce((sum: Decimal, currentRevenue: number) => !!currentRevenue ? sum.add(currentRevenue) : sum, new Decimal(0));
      return MathUtils.roundDecimalCurrencyToCents(totalRevenue).toNumber();
    });
  }

  vendorTotalCommission(eventPartyId: number): Observable<number> {
    return this.rsvps.map((filteredList: List<Rsvp>) => {
      return filteredList.filter((filtered: Rsvp) => filtered.eventPartyId === eventPartyId).toList();
    }).map(rsvpList => {
      const totalCommission = rsvpList
        .map(rsvp => rsvp.commission)
        .reduce((sum: Decimal, current: number) => !!current ? sum.add(current) : sum, new Decimal(0));
      return MathUtils.roundDecimalCurrencyToCents(totalCommission).toNumber();
    });
  }

  addRsvp(rsvp: Rsvp): Observable<any> {
    return this.rsvpHttpService.add(rsvp)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_RSVP', `Error adding rsvp: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.RSVPS.RSVP_ADDED');
        return result;
      });
  }

  importRsvpFile(eventId: number, eventPartyId: number, file: File): Observable<any> {
    return this.rsvpHttpService
      .importRsvpFile(eventId, eventPartyId, file);
  }

  deleteRsvp(rsvp: Rsvp): Observable<any> {
    return this.rsvpHttpService.delete(rsvp)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.DELETE_RSVP', `Error adding rsvp: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.RSVPS.RSVP_DELETED');
        return result;
      });
  }

  updateRsvp(rsvp: Rsvp): Observable<any> {
    return this.rsvpHttpService.update(rsvp)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_RSVP', `Error updating rsvp: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success(
          'EVENT.RSVPS.RSVP_UPDATED', null,
          {
            lastName: String (rsvp.lastName),
            firstName: !rsvp.firstName ? '' : String (`, ${rsvp.firstName}`)
          });
        return result;
      });
  }

  /**
   * Has to be called to retrieve RSVPs.
   * Is called by resolver.
   *
   * @param eventId
   * @returns RSVP list
   */
  loadRsvps(eventId: number): Observable<List<Rsvp>> {
    return this.rsvpHttpService.getRsvps(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RSVPS',
          `Error retrieving RSVPs: ${ error }`
        );
        return Observable.throw(error);
      })
      .map((rsvpList: List<Rsvp>) => {
        this.rsvpSubject.next(rsvpList);

        this.reloadEventHasRsvpsCache(eventId).pipe(take(1)).subscribe();

        return rsvpList;
      });
  }

  /**
   * Has to be called to retrieve RSVPs.
   * Is called by resolver.
   *
   * @param eventId
   * @param eventPartyId
   * @returns RSVP list
   */
  loadRsvpsByParty(eventId: number, eventPartyId: number): Observable<List<Rsvp>> {
    return this.rsvpHttpService.getRsvpsByParty(eventId, eventPartyId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RSVPS',
          `Error retrieving RSVPs: ${ error }`
        );
        return Observable.throw(error);
      })
      .map((rsvpList: List<Rsvp>) => {
        this.rsvpSubject.next(rsvpList);

        this.reloadEventHasRsvpsCache(eventId, eventPartyId).pipe(take(1)).subscribe();

        return rsvpList;
      });
  }

  /**
   * Loads the RSVPs paginated
   *
   * @param eventId the id of the event
   * @param page the page to be load
   * @param size the number of RSVP to be load
   * @returns an `Page` of RSVP
   */
  loadRsvpsPaginated(eventId: number, page: number, size: number): Observable<Page<Rsvp>> {
    return this.rsvpHttpService.getRsvpsPaginated(eventId, page, size)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RSVPS',
          `Error retrieving RSVPs: ${error}`
        );
        return Observable.throw(error);
      })
      .map((rsvpPage: Page<Rsvp>) => {
        this.searchResultsPage.next(rsvpPage);
        return rsvpPage;
      });
  }

  refreshRsvpsSummary(eventId: number, pagesize: number) {
    this.loadRsvpsPaginated(eventId, Constants.SEARCH_DEFAULT_PAGE, pagesize).take(1).subscribe();
  }

  /**
   * Transform the RSVP list into a room stay summary
   *
   * @param statusCode{ReservationStatusCode[]}
   * @returns {Observable<StaySummary>}
   */
  getRoomStaySummary(): Observable<RoomStaySummary> {
    return this.rsvps.map(rsvpList => {
      return TransactionUtils.transformTransactionsToSummary(rsvpList, this.translateService);
    });
  }

  getVendorRoomStaySummary(eventPartyId: number): Observable<RoomStaySummary> {
    return this.rsvps.map(rsvpList => {
      return TransactionUtils.transformTransactionsToSummary(rsvpList.filter(
        rsvp => rsvp.eventPartyId === eventPartyId).toList(), this.translateService);
    });
  }

  getRsvpsFromEvent(eventId: number, page: number, size: number): Observable<Page<Rsvp>> {
    return this.rsvpHttpService.getRsvpsPaginated(eventId, page, size)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RSVPS',
          `Error retrieving RSVPs: ${error}`
        );
        return Observable.throw(error);
      });
  }

  getRsvpsFromVendor(eventId: number, eventPartyId: number, page: number, size: number): Observable<Page<Rsvp>> {
    return this.rsvpHttpService.getRsvpsByVendorPaginated(eventId, eventPartyId, page, size)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RSVPS',
          `Error retrieving RSVPs: ${error}`
        );
        return Observable.throw(error);
      });
  }

  getEventHasRsvps(): Observable<boolean> {
    return super.getEventHasItems();
  }

  getHasRsvpsByEventPartyId(eventPartyId: number): Observable<boolean> {
    return super.getHasItemsByEventPartyId(eventPartyId);
  }

  loadEventHasRsvpsCache(eventHasRsvpsByVendor: { [key: number]: boolean; }): void {
    super.loadEventHasItemsCache(eventHasRsvpsByVendor);
  }

  reloadEventHasRsvpsCache(eventId: number, eventPartyId?: number): Observable<EventHasItemsByVendor> {
    let serviceCall: Observable<EventHasItemsByVendor | Response>;
    if (!!eventPartyId) {
      serviceCall = this.eventHttpService.fetchHasItemsByParty(eventId, eventPartyId);
    } else {
      serviceCall = this.eventHttpService.fetchHasItems(eventId);
    }

    return serviceCall.pipe(
      catchError((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_CHECK_HAS_RSVPS',
          `Error retrieving items: ${ error }`
        );
        return Observable.throw(error);
      }),
      filter((response: EventHasItemsByVendor | Response) => response instanceof EventHasItemsByVendor),
      tap((eventHasItemsByVendor: EventHasItemsByVendor) => {
        this.loadEventHasRsvpsCache(eventHasItemsByVendor.hasRsvpsByVendor);
      })
    );
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/rsvp.store.ts